import { Bell, Boxes, Command, House, LayoutDashboard, Map, Menu, Plus, Search, Settings, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { Project, Session } from '../../shared/types';
import { QuasarMark } from './Quasar';
import { QuickCapture } from './QuickCapture';

const nav = [
  { to: '/', label: 'Command center', icon: LayoutDashboard },
  { to: '/projects', label: 'Projects', icon: Boxes },
  { to: '/projects?view=map', label: 'Constellation', icon: Map },
  { to: '/search', label: 'Search', icon: Search },
  { to: '/inbox', label: 'Inbox', icon: Bell },
  { to: '/settings', label: 'Settings', icon: Settings },
];

export function Shell({ session, projects }: { session: Session; projects: Project[] }) {
  const [capture, setCapture] = useState(false);
  const [palette, setPalette] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const isMap = location.pathname === "/projects" && new URLSearchParams(location.search).get("view") === "map";
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); setPalette(true); }
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') { event.preventDefault(); setCapture(true); }
      if (event.key === 'Escape') { setPalette(false); setCapture(false); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  const matches = projects.filter((project) => `${project.name} ${project.summary} ${project.tags.join(' ')}`.toLowerCase().includes(query.toLowerCase())).slice(0, 6);
  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileNav ? 'is-open' : ''}`}>
        <div className="brand-lockup"><div className="brand-mark"><QuasarMark /></div><div><strong>Babel Foundry</strong><span>Project workstation</span></div></div>
        <button className="capture-button" onClick={() => setCapture(true)}><Plus size={17} /> Quick capture <kbd>⌘↵</kbd></button>
        <nav aria-label="Primary navigation">
          {nav.map((item) => <Link key={item.label} to={item.to} className={(item.label === "Constellation" ? isMap : item.label === "Projects" ? location.pathname.startsWith("/projects") && !isMap : location.pathname === item.to) ? "active" : undefined} onClick={() => setMobileNav(false)}><item.icon size={18} /><span>{item.label}</span></Link>)}
        </nav>
        <div className="sidebar-foot">
          <div className="system-pulse"><span /> <div><strong>Foundry online</strong><small>{session.environment}</small></div></div>
          <div className="member-chip"><span>{session.displayName.slice(0, 1).toUpperCase()}</span><div><strong>{session.displayName}</strong><small>{session.role}</small></div></div>
        </div>
      </aside>
      <main className="main-stage">
        <header className="topbar">
          <button className="icon-button mobile-menu" onClick={() => setMobileNav(!mobileNav)} aria-label="Toggle navigation">{mobileNav ? <X /> : <Menu />}</button>
          <button className="command-trigger" onClick={() => setPalette(true)}><Search size={17} /><span>Search projects, notes, decisions…</span><kbd>Ctrl K</kbd></button>
          <a className="suite-return" href="https://zeakryy.win/" aria-label="Back to home suite - zeakryy.win" title="Back to zeakryy.win"><House size={17}/><span>Home suite</span></a>
          <Link className="topbar-action" to="/inbox"><Bell size={18} /><span>Inbox</span></Link>
        </header>
        <Outlet />
      </main>
      <QuickCapture projects={projects} open={capture} onClose={() => setCapture(false)} />
      {palette && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setPalette(false)}>
          <section className="command-palette" role="dialog" aria-modal="true" aria-label="Command palette">
            <div className="command-input"><Command size={19} /><input autoFocus placeholder="Type a project or command…" value={query} onChange={(event) => setQuery(event.target.value)} /></div>
            <div className="command-results">
              <span className="mono eyebrow">PROJECTS</span>
              {matches.map((project) => <button key={project.id} onClick={() => { navigate(`/projects/${project.slug}/overview`); setPalette(false); setQuery(''); }}><span className={`result-dot accent-${project.accent}`} /><div><strong>{project.name}</strong><small>{project.currentFocus}</small></div></button>)}
              <span className="mono eyebrow">COMMANDS</span>
              <button onClick={() => { setPalette(false); setCapture(true); }}><Plus size={16} /><div><strong>Quick capture</strong><small>Add a task, blocker, improvement, or future feature</small></div></button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
